/*
 * Inline icons. The original component pulled Phosphor from unpkg; hosting our
 * own means no third-party origin in the CSP and no icon flash on first paint.
 * All of them are 24x24, stroke-based, and inherit currentColor.
 */
type Props = { size?: number; className?: string; strokeWidth?: number }

const Svg = ({ size = 16, className, strokeWidth = 1.7, children }: Props & { children: React.ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
)

export const GitBranch = (p: Props) => (
  <Svg {...p}>
    <circle cx="6" cy="5" r="2.5" />
    <circle cx="6" cy="19" r="2.5" />
    <circle cx="18" cy="8" r="2.5" />
    <path d="M6 7.5v9M6 12h6a6 6 0 0 0 6-6.1" />
  </Svg>
)

export const PullRequestIcon = (p: Props) => (
  <Svg {...p}>
    <circle cx="6" cy="6" r="2.5" />
    <circle cx="6" cy="19" r="2.5" />
    <circle cx="18" cy="18" r="2.5" />
    <path d="M6 8.5v8M18 15.5V9a3 3 0 0 0-3-3h-3.5" />
    <path d="M13.5 3.5 11 6l2.5 2.5" />
  </Svg>
)

export const CaretDown = (p: Props) => (<Svg {...p}><path d="m6 9 6 6 6-6" /></Svg>)
export const CaretRight = (p: Props) => (<Svg {...p}><path d="m9 6 6 6-6 6" /></Svg>)
export const ExternalLink = (p: Props) => (<Svg {...p}><path d="M7 17 17 7M9 7h8v8" /></Svg>)

export const Lock = (p: Props) => (
  <Svg {...p}>
    <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" fill="currentColor" stroke="none" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </Svg>
)

export const Sun = (p: Props) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2.5M12 19.5V22M4.2 4.2l1.8 1.8M18 18l1.8 1.8M2 12h2.5M19.5 12H22M4.2 19.8 6 18M18 6l1.8-1.8" />
  </Svg>
)

export const Moon = (p: Props) => (<Svg {...p}><path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" /></Svg>)

export const CheckCircle = (p: Props) => (<Svg {...p}><circle cx="12" cy="12" r="9" /><path d="m8.2 12.3 2.6 2.6 5-5.4" /></Svg>)
export const XCircle = (p: Props) => (<Svg {...p}><circle cx="12" cy="12" r="9" /><path d="m9 9 6 6M15 9l-6 6" /></Svg>)
export const Clock = (p: Props) => (<Svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 1.8" /></Svg>)
export const DotCircle = (p: Props) => (<Svg {...p}><circle cx="12" cy="12" r="9" strokeDasharray="2.5 3" /></Svg>)

export const Sync = (p: Props) => (
  <Svg {...p}>
    <path d="M20 12a8 8 0 0 1-13.6 5.7M4 12a8 8 0 0 1 13.6-5.7" />
    <path d="M17.6 3v3.3h-3.3M6.4 21v-3.3h3.3" />
  </Svg>
)

export const Clipboard = (p: Props) => (
  <Svg {...p}>
    <rect x="8" y="3.5" width="8" height="3.5" rx="1" />
    <path d="M8 5.2H6.5A1.5 1.5 0 0 0 5 6.7v12.3a1.5 1.5 0 0 0 1.5 1.5h11a1.5 1.5 0 0 0 1.5-1.5V6.7a1.5 1.5 0 0 0-1.5-1.5H16" />
  </Svg>
)

export const Search = (p: Props) => (<Svg {...p}><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4.5 4.5" /></Svg>)

export const SignOut = (p: Props) => (
  <Svg {...p}><path d="M14 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8" /><path d="M17 8.5 20.5 12 17 15.5M20.5 12H10" /></Svg>
)

export const GitHub = (p: Props) => (
  <svg width={p.size ?? 16} height={p.size ?? 16} viewBox="0 0 24 24" fill="currentColor" className={p.className} aria-hidden="true">
    <path d="M12 1.5A10.5 10.5 0 0 0 1.5 12c0 4.64 3.01 8.57 7.18 9.96.53.1.72-.23.72-.5 0-.25-.01-1.07-.01-1.94-2.63.48-3.32-.64-3.53-1.23-.12-.3-.63-1.23-1.08-1.48-.37-.2-.9-.68-.02-.7.83-.01 1.42.76 1.62 1.08.95 1.6 2.46 1.15 3.06.87.1-.68.37-1.15.67-1.41-2.33-.26-4.77-1.17-4.77-5.18 0-1.14.4-2.08 1.08-2.82-.11-.26-.47-1.34.1-2.78 0 0 .88-.28 2.88 1.07a9.7 9.7 0 0 1 5.24 0c2-1.36 2.88-1.07 2.88-1.07.57 1.44.21 2.52.1 2.78.68.74 1.08 1.67 1.08 2.82 0 4.02-2.45 4.92-4.78 5.18.38.33.71.96.71 1.94 0 1.4-.01 2.53-.01 2.88 0 .28.19.61.72.5A10.5 10.5 0 0 0 22.5 12 10.5 10.5 0 0 0 12 1.5Z" />
  </svg>
)

export const Warning = (p: Props) => (
  <Svg {...p}><path d="M12 4.5 21 20H3l9-15.5Z" /><path d="M12 10v4.2M12 17.2v.2" /></Svg>
)

export const Shield = (p: Props) => (<Svg {...p}><path d="M12 3 4.5 6v6c0 4.4 3.1 7.6 7.5 9 4.4-1.4 7.5-4.6 7.5-9V6L12 3Z" /><path d="m8.8 12 2.2 2.2 4.2-4.4" /></Svg>)
export const Merge = (p: Props) => (<Svg {...p}><circle cx="7" cy="18" r="2.5" /><circle cx="7" cy="6" r="2.5" /><circle cx="17" cy="12" r="2.5" /><path d="M7 8.5v7M7 9a5 5 0 0 0 5 5h2.5" /></Svg>)
export const Archive = (p: Props) => (<Svg {...p}><rect x="3.5" y="4.5" width="17" height="4" rx="1" /><path d="M5 8.5V19a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8.5M10 12.5h4" /></Svg>)
